/**
 * Sound Service
 * Web Audio API wrapper for game audio playback
 *
 * Features:
 * - Looping sounds with seamless playback
 * - Stereo panning control
 * - Play position tracking
 * - Volume control (master + per-category)
 * - Lazy loading with caching
 * - Fade in/out support
 *
 * Supported Formats:
 * - OGG Vorbis (.ogg) - Recommended for web (good compression, wide support)
 * - FLAC (.flac) - Lossless audio (larger files, better quality)
 * - Also supports: WAV, MP3, AAC, WebM (browser-dependent)
 * - All formats are decoded via Web Audio API's decodeAudioData()
 */

import type { SoundOptions, SoundInstance, SoundCategory } from '../types/audio';
import { soundStore } from '../stores/soundStore';

class SoundService {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private categoryGains: Map<SoundCategory, GainNode> = new Map();
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private activeSounds: Map<string, SoundInstance> = new Map();
  private nextSoundId = 0;

  /**
   * Initialize the audio system
   * Must be called after user interaction (browser security requirement)
   */
  async initialize(): Promise<void> {
    if (this.audioContext) {
      return; // Already initialized
    }

    try {
      // Create AudioContext
      this.audioContext = new AudioContext();

      // Create master gain node
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);

      // Create category gain nodes
      const categories = [
        'music' as SoundCategory,
        'sfx' as SoundCategory,
        'ambient' as SoundCategory,
        'ui' as SoundCategory,
        'voice' as SoundCategory,
      ];

      categories.forEach((category) => {
        const gainNode = this.audioContext!.createGain();
        gainNode.connect(this.masterGain!);
        this.categoryGains.set(category, gainNode);
      });

      // Resume context if suspended (user interaction requirement)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      soundStore.setInitialized(true);
      soundStore.setSuspended(this.audioContext.state === 'suspended');

      console.log('Audio system initialized');
    } catch (error) {
      console.error('Failed to initialize audio system:', error);
      throw error;
    }
  }

  /**
   * Resume AudioContext after user interaction
   */
  async resume(): Promise<void> {
    if (!this.audioContext) {
      await this.initialize();
      return;
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      soundStore.setSuspended(false);
      console.log('AudioContext resumed');
    }
  }

  /**
   * Play a sound from file path
   * Returns sound instance ID for tracking/control
   */
  async play(filePath: string, options: SoundOptions = {}): Promise<string | null> {
    if (!this.audioContext || !this.masterGain) {
      console.warn('Audio system not initialized');
      return null;
    }

    try {
      // Get or load audio buffer
      const buffer = await this.getAudioBuffer(filePath);
      if (!buffer) return null;

      // Generate unique ID
      const id = `sound_${this.nextSoundId++}`;

      // Create audio nodes
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.loop = options.loop ?? false;

      // Create gain node for this sound
      const gainNode = this.audioContext.createGain();
      const initialVolume = options.volume ?? 1.0;
      gainNode.gain.value = options.fadeIn ? 0 : initialVolume;

      // Create panner node
      const panNode = this.audioContext.createStereoPanner();
      panNode.pan.value = options.pan ?? 0;

      // Connect audio graph: source -> gain -> pan -> category -> master
      source.connect(gainNode);
      gainNode.connect(panNode);

      const category = options.category ?? ('sfx' as SoundCategory);
      const categoryGain = this.categoryGains.get(category);
      if (categoryGain) {
        panNode.connect(categoryGain);
      } else {
        panNode.connect(this.masterGain);
      }

      // Handle fade in
      if (options.fadeIn && options.fadeIn > 0) {
        const fadeTime = options.fadeIn / 1000; // Convert to seconds
        gainNode.gain.linearRampToValueAtTime(
          initialVolume,
          this.audioContext.currentTime + fadeTime
        );
      }

      // Start playback
      const startTime = options.startTime ?? 0;
      source.start(0, startTime);

      // Track instance
      const instance: SoundInstance = {
        id,
        source,
        gainNode,
        panNode,
        category,
        isLooping: source.loop,
        startedAt: this.audioContext.currentTime - startTime,
        isPaused: false,
        pauseOffset: 0,
        buffer,
        filePath,
      };

      this.activeSounds.set(id, instance);
      soundStore.addActiveSound(id, instance);

      // Auto-cleanup when sound ends (if not looping)
      if (!source.loop) {
        source.onended = () => {
          this.cleanup(id);
        };
      }

      return id;
    } catch (error) {
      console.error(`Failed to play sound: ${filePath}`, error);
      return null;
    }
  }

  /**
   * Stop a sound by ID
   */
  async stop(soundId: string, fadeOut?: number): Promise<void> {
    const instance = this.activeSounds.get(soundId);
    if (!instance || !this.audioContext) return;

    try {
      if (fadeOut && fadeOut > 0) {
        // Fade out before stopping
        const fadeTime = fadeOut / 1000;
        const currentTime = this.audioContext.currentTime;
        instance.gainNode.gain.linearRampToValueAtTime(0, currentTime + fadeTime);

        // Stop after fade completes
        setTimeout(() => {
          instance.source.stop();
          this.cleanup(soundId);
        }, fadeOut);
      } else {
        // Immediate stop
        instance.source.stop();
        this.cleanup(soundId);
      }
    } catch (error) {
      console.error(`Failed to stop sound ${soundId}:`, error);
      this.cleanup(soundId);
    }
  }

  /**
   * Stop all sounds in a category
   */
  async stopCategory(category: SoundCategory, fadeOut?: number): Promise<void> {
    const soundsToStop: string[] = [];

    this.activeSounds.forEach((instance, id) => {
      if (instance.category === category) {
        soundsToStop.push(id);
      }
    });

    await Promise.all(soundsToStop.map((id) => this.stop(id, fadeOut)));
  }

  /**
   * Stop all sounds
   */
  async stopAll(fadeOut?: number): Promise<void> {
    const allSoundIds = Array.from(this.activeSounds.keys());
    await Promise.all(allSoundIds.map((id) => this.stop(id, fadeOut)));
  }

  /**
   * Pause a sound (can be resumed)
   */
  pause(soundId: string): void {
    const instance = this.activeSounds.get(soundId);
    if (!instance || !this.audioContext || instance.isPaused) return;

    // Calculate current play position
    const elapsed = this.audioContext.currentTime - instance.startedAt;
    instance.pauseOffset = elapsed % instance.buffer.duration;
    instance.isPaused = true;

    // Stop the source
    instance.source.stop();
  }

  /**
   * Resume a paused sound
   */
  async resumeSound(soundId: string): Promise<void> {
    const instance = this.activeSounds.get(soundId);
    if (!instance || !this.audioContext || !instance.isPaused) return;

    try {
      // Create new source (sources can't be reused)
      const newSource = this.audioContext.createBufferSource();
      newSource.buffer = instance.buffer;
      newSource.loop = instance.isLooping;

      // Reconnect audio graph
      newSource.connect(instance.gainNode);

      // Start from paused position
      newSource.start(0, instance.pauseOffset);

      // Update instance
      instance.source = newSource;
      instance.startedAt = this.audioContext.currentTime - instance.pauseOffset;
      instance.isPaused = false;
      instance.pauseOffset = 0;

      // Re-attach onended handler
      if (!newSource.loop) {
        newSource.onended = () => {
          this.cleanup(soundId);
        };
      }
    } catch (error) {
      console.error(`Failed to resume sound ${soundId}:`, error);
      this.cleanup(soundId);
    }
  }

  /**
   * Get current playback position in seconds
   */
  getPosition(soundId: string): number | null {
    const instance = this.activeSounds.get(soundId);
    if (!instance || !this.audioContext) return null;

    if (instance.isPaused) {
      return instance.pauseOffset;
    }

    const elapsed = this.audioContext.currentTime - instance.startedAt;
    return instance.isLooping ? elapsed % instance.buffer.duration : elapsed;
  }

  /**
   * Get sound duration in seconds
   */
  getDuration(soundId: string): number | null {
    const instance = this.activeSounds.get(soundId);
    return instance ? instance.buffer.duration : null;
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setMasterVolume(volume: number): void {
    if (!this.masterGain) return;

    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.masterGain.gain.value = clampedVolume;
    soundStore.setMasterVolume(clampedVolume);
  }

  /**
   * Set category volume (0.0 to 1.0)
   */
  setCategoryVolume(category: SoundCategory, volume: number): void {
    const gainNode = this.categoryGains.get(category);
    if (!gainNode) return;

    const clampedVolume = Math.max(0, Math.min(1, volume));
    gainNode.gain.value = clampedVolume;
    soundStore.setCategoryVolume(category, clampedVolume);
  }

  /**
   * Mute/unmute all audio
   */
  setMuted(muted: boolean): void {
    if (!this.masterGain) return;

    this.masterGain.gain.value = muted ? 0 : soundStore.getMasterVolume();
    soundStore.setMuted(muted);
  }

  /**
   * Set stereo pan for a sound (-1.0 to 1.0)
   */
  setPan(soundId: string, pan: number): void {
    const instance = this.activeSounds.get(soundId);
    if (!instance) return;

    const clampedPan = Math.max(-1, Math.min(1, pan));
    instance.panNode.pan.value = clampedPan;
  }

  /**
   * Preload audio files for faster playback
   */
  async preload(filePaths: string[]): Promise<void> {
    await Promise.all(filePaths.map((path) => this.getAudioBuffer(path)));
  }

  /**
   * Get or load audio buffer (with caching)
   */
  private async getAudioBuffer(filePath: string): Promise<AudioBuffer | null> {
    // Check cache
    if (this.audioBuffers.has(filePath)) {
      return this.audioBuffers.get(filePath)!;
    }

    if (!this.audioContext) {
      console.warn('AudioContext not initialized');
      return null;
    }

    try {
      // Fetch audio file
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      // Decode audio data
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Cache buffer
      this.audioBuffers.set(filePath, audioBuffer);

      return audioBuffer;
    } catch (error) {
      console.error(`Failed to load audio: ${filePath}`, error);
      return null;
    }
  }

  /**
   * Clean up sound instance
   */
  private cleanup(soundId: string): void {
    const instance = this.activeSounds.get(soundId);
    if (instance) {
      // Disconnect nodes
      try {
        instance.source.disconnect();
        instance.gainNode.disconnect();
        instance.panNode.disconnect();
      } catch (error) {
        // Already disconnected, ignore
      }

      // Remove from tracking
      this.activeSounds.delete(soundId);
      soundStore.removeActiveSound(soundId);
    }
  }

  /**
   * Get all active sound IDs
   */
  getActiveSounds(): string[] {
    return Array.from(this.activeSounds.keys());
  }

  /**
   * Check if audio system is initialized
   */
  isInitialized(): boolean {
    return this.audioContext !== null;
  }

  /**
   * Check if AudioContext is suspended
   */
  isSuspended(): boolean {
    if (!this.audioContext) return true;
    return this.audioContext.state === 'suspended';
  }
}

export const soundService = new SoundService();
